import { useRightPanel } from '@/contexts/RightPanelContext';
import { Button } from '@/components/ui/button';
import { RotateCcw, GripVertical, Eye, EyeOff, Columns3 } from 'lucide-react';
import { DEFAULT_AUDIT_HISTORY_COLUMNS } from '@/lib/audit-history-columns-config';
import { Input } from '@/components/ui/input';
import { useState } from 'react';

export function AuditHistoryColumnsTab() {
  const { visibleColumns: contextVisibleColumns, setVisibleColumns, columnOrder: contextColumnOrder, setColumnOrder } = useRightPanel();
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Use context columns or fall back to defaults (matching the audit history page logic)
  const visibleColumns = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_AUDIT_HISTORY_COLUMNS.filter(col => col.visible).map(col => col.id);

  const columnOrder = contextColumnOrder.length > 0
    ? contextColumnOrder
    : DEFAULT_AUDIT_HISTORY_COLUMNS.map(col => col.id);

  // Use the column order from context, or fall back to default order
  const orderedColumns = columnOrder.length > 0 
    ? columnOrder.map(id => DEFAULT_AUDIT_HISTORY_COLUMNS.find(col => col.id === id)).filter(Boolean)
    : DEFAULT_AUDIT_HISTORY_COLUMNS;

  // Filter columns based on search
  const filteredColumns = orderedColumns.filter(col => 
    col && col.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleColumn = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      setVisibleColumns(visibleColumns.filter(id => id !== columnId));
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };

  const handleResetColumns = () => {
    const defaultVisible = DEFAULT_AUDIT_HISTORY_COLUMNS
      .filter(col => col.visible)
      .map(col => col.id);
    setVisibleColumns(defaultVisible);
    setColumnOrder(DEFAULT_AUDIT_HISTORY_COLUMNS.map(col => col.id));
  };

  const handleShowAll = () => {
    setVisibleColumns(DEFAULT_AUDIT_HISTORY_COLUMNS.map(col => col.id));
  };

  const handleDragStart= (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...orderedColumns];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);

    const newColumnOrder = newOrder.map(col => col?.id).filter((id): id is string => id !== undefined);
    setColumnOrder(newColumnOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const visibleCount = visibleColumns.length;
  const totalCount = DEFAULT_AUDIT_HISTORY_COLUMNS.length;
  const allVisible = visibleCount === totalCount;

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b bg-muted/30">
        {/* Search */}
        <div className="relative">
          <Columns3 className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex-shrink-0 px-4 py-2 border-b bg-muted/10">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShowAll}
            disabled={allVisible}
            className="h-7 px-2 text-xs flex-1"
          >
            <Eye className="mr-1.5 h-3 w-3" />
            Show All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetColumns}
            className="h-7 px-2 text-xs flex-1"
          >
            <RotateCcw className="mr-1.5 h-3 w-3" />
            Reset
          </Button>
        </div>
      </div>

      {/* Scrollable Column List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-1">
          {filteredColumns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No columns found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          ) : (
            filteredColumns.map((column: any, index: number) => {
              const isVisible = visibleColumns.includes(column.id);
              return (
                <div
                  key={column.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`
                    flex items-center justify-between gap-2.5 py-2 px-2.5 rounded-md 
                    transition-all duration-150 cursor-move
                    ${isVisible 
                      ? 'bg-primary/5 hover:bg-primary/10 border border-primary/20' 
                      : 'hover:bg-muted/50 border border-transparent'
                    }
                    ${draggedIndex === index ? 'opacity-50' : ''}
                  `}
                >
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                    <span
                      className={`
                        text-sm leading-none select-none truncate
                        ${isVisible ? 'font-medium text-foreground' : 'font-normal text-muted-foreground'}
                      `}
                    >
                      {column.label}
                    </span>
                  </div>
                  <Button
                    variant={isVisible ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleToggleColumn(column.id)}
                    className={`h-7 w-7 p-0 flex-shrink-0 ${isVisible ? '' : 'hover:bg-muted'}`}
                  >
                    {isVisible ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Footer Hint */}
      <div className="flex-shrink-0 border-t bg-muted/20">
        <div className="px-4 py-3">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <GripVertical className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <p className="leading-relaxed">
              Drag columns to reorder them in the table. Changes are saved automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
